import React from 'react';
import { StatusPill, MemberAvatarList, CardActionsMenu, getStatusVariant } from '@/shared/components/ui';
import type { CardAction, StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { MiniGantt } from './MiniGantt';
import type { ProjectListItem, ProjectStatus } from '../types';

interface UserLite {
  id: string;
  name: string;
  avatar?: string;
}

/**
 * Mapeia o status do projeto para o `StatusPill` shared (F62/F65: 'Concluído' =
 * verde via semântica `completed`). Exportado para reuso na tabela da
 * `ProjectsPage` e no workspace.
 */
export function getProjectStatusPill(
  status: ProjectStatus,
): { variant: StatusPillVariant; label: string } {
  switch (status) {
    case 'active':
      return { variant: getStatusVariant('active'), label: 'Ativo' };
    case 'completed':
      return { variant: getStatusVariant('completed'), label: 'Concluído' };
    case 'archived':
    default:
      return { variant: getStatusVariant('archived'), label: 'Arquivado' };
  }
}

/** dd/mm (ou "—" quando ausente). */
export function fmtShortDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export interface ProjectCardProps {
  /** Item de listagem. */
  project: ProjectListItem;
  /** Nome do pipeline resolvido (via `usePipelinesDropdown` na página). */
  pipelineName?: string;
  /** Mapa userId → dados leves (via `useUsersDropdown` na página). */
  usersById: Map<string, UserLite>;
  /** Navega para o workspace do projeto. */
  onOpen: () => void;
  /** Arquiva (status `active/completed`) ou restaura (status `archived`). */
  onArchiveToggle?: (id: string, status: ProjectStatus) => void;
  /** Exclui o projeto. */
  onDelete?: (id: string) => void;
}

/**
 * Card de projeto (view "Cards") — dot + nome/cliente + pill de status/atrasadas,
 * mini-timeline (`MiniGantt`), período e rodapé com avatares do time, progresso
 * e pipeline. SEM checkbox de seleção (decisão F34 — seleção só na tabela);
 * menu ••• de ações sempre visível. Avatares sem foto = slate (default do
 * `MemberAvatarList`).
 */
export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  pipelineName,
  usersById,
  onOpen,
  onArchiveToggle,
  onDelete,
}) => {
  const isActive = project.status === 'active';
  const isArchived = project.status === 'archived';
  const done = project.progress?.done ?? 0;
  const total = project.progress?.total ?? 0;
  const pct = Math.max(0, Math.min(100, project.progress?.pct ?? 0));
  const pill = getProjectStatusPill(project.status);

  // Time = líder + membros (dedup), resolvidos para {id, name, avatar}.
  const teamIds = [project.leaderId, ...(project.memberIds ?? [])].filter(
    (id, i, arr) => !!id && arr.indexOf(id) === i,
  );
  const team = teamIds
    .map((id) => usersById.get(id))
    .filter((u): u is UserLite => !!u);

  const actions: CardAction[] = [
    { label: 'Abrir workspace', icon: 'arrow-right', onClick: onOpen },
  ];
  if (onArchiveToggle) {
    actions.push({
      label: isArchived ? 'Restaurar' : 'Arquivar',
      icon: 'archive',
      onClick: () => onArchiveToggle(project.id, project.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir projeto', icon: 'trash', onClick: () => onDelete(project.id), variant: 'danger' });
  }

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-pointer"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
        ...(isActive ? {} : { opacity: 0.72 }),
      }}
      onClick={onOpen}
      data-testid={`projects-card-${project.id}`}
    >
      {/* Header: dot + nome/cliente + status/atrasadas + menu */}
      <div className="flex items-start gap-2.5">
        <span
          className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
          style={{ backgroundColor: project.color }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold tracking-tight truncate" style={{ color: 'var(--theme-card-text)' }}>
            {project.name}
          </div>
          {project.clientName && (
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {project.clientName}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {project.overdueCount > 0 ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full font-medium"
              style={{ fontSize: 10, backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
            >
              {project.overdueCount} atrasada{project.overdueCount > 1 ? 's' : ''}
            </span>
          ) : (
            /* F66: dot uniforme (default) nas 3 views */
            <StatusPill variant={pill.variant} label={pill.label} data-testid={`projects-card-status-${project.id}`} />
          )}
          {actions.length > 0 && (
            <div className="-mr-1" onClick={(e) => e.stopPropagation()}>
              <CardActionsMenu actions={actions} size="sm" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-2.5">
        <MiniGantt project={project} />
      </div>

      {/* Período — ícone de calendário + intervalo (.pj-meta do mockup) */}
      <div
        className="flex items-center gap-1.5 mt-1.5 min-w-0"
        style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}
      >
        <Icon name="calendar" className="w-3.5 h-3.5 flex-none" />
        <span className="truncate">
          {fmtShortDate(project.startDate)} – {fmtShortDate(project.endDate)}
        </span>
      </div>

      {/* Próximo marco / tarefas atrasadas (.pj-meta + nextLine do mockup) */}
      <div className="flex items-center gap-1.5 mt-1 min-w-0" style={{ fontSize: 11 }}>
        {project.overdueCount > 0 ? (
          <span className="inline-flex items-center gap-1 font-semibold" style={{ color: 'var(--theme-error-color)' }}>
            <Icon name="alert-triangle" className="w-3.5 h-3.5 flex-none" />
            {project.overdueCount} tarefa{project.overdueCount > 1 ? 's' : ''} atrasada{project.overdueCount > 1 ? 's' : ''}
          </span>
        ) : project.nextMilestone ? (
          <span className="inline-flex items-center gap-1 min-w-0" style={{ color: 'var(--theme-muted-text)' }}>
            <Icon name="flag" className="w-3.5 h-3.5 flex-none" />
            <span className="truncate">
              Próx: {project.nextMilestone.title} · {fmtShortDate(project.nextMilestone.date)}
            </span>
          </span>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>Sem marcos pendentes</span>
        )}
      </div>

      {/* Progresso — linha própria (barra + pct), acima do rodapé */}
      <div className="flex items-center gap-2.5 mt-2.5">
        <div
          className="flex-1 h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--theme-border)' }}
        >
          <div
            className="h-full rounded-full"
            style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'var(--theme-success-color)' : 'var(--theme-primary)' }}
          />
        </div>
        <span
          className="flex-none whitespace-nowrap tabular-nums font-semibold"
          style={{ fontSize: 12, color: 'var(--theme-card-text)' }}
        >
          {done}/{total} · {pct}%
        </span>
      </div>

      {/* Rodapé: time (esq) + chip de pipeline (dir) */}
      <div
        className="flex items-center justify-between gap-2.5 mt-2.5 pt-2.5 border-t"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        {team.length > 0 ? (
          <MemberAvatarList members={team} canRemoveMembers={false} size="sm" limit={4} />
        ) : (
          <span aria-hidden />
        )}
        {pipelineName && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full max-w-[150px]"
            style={{ fontSize: 12, padding: '3px 9px', backgroundColor: 'var(--theme-border)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="trending-up" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
            <span className="truncate">{pipelineName}</span>
          </span>
        )}
      </div>
    </div>
  );
};

ProjectCard.displayName = 'ProjectCard';
